// src/app/modules/events/EventManagement.jsx
import React, {useEffect, useMemo, useRef, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import {
    fetchEventsRequest,
    createEventRequest,
    updateEventRequest,
    deleteEventRequest,
    cancelEventRequest,
    replanifyEventRequest,
    exportBudgetRequest,
    exportFormationsRequest,
    importEventsExcelRequest,
    exportEventsExcelRequest,
    downloadEventsTemplateRequest,
    downloadEventAttendanceSheetRequest,
    sendEventCalendarRequest,
} from "../../../../../redux/actions/eventsActions";

import { fetchModulesCleRequest }        from "../../../../../redux/actions/modulesCleActions";
import { fetchBudgetByCampagneRequest }  from "../../../../../redux/actions/campagnesActions";
import { fetchSallesRequest }            from "../../../../../redux/actions/sallesActions";
import { fetchConsultantsRequest }       from "../../../../../redux/actions/consultantsActions";
import { getAllEvaluationsRequest }      from "../../../../../redux/actions/evaluationActions";
import { fetchAssignedUsersByEventRequest } from "../../../../../redux/actions/assignmentActions";
import "../../RessourcesStyles/EventManagement.css";
import BulkAssignmentModal from "../assignments/BulkAssignmentModal";
import EventDetailsModal from "./EventDetailsModal";
import EventFormModal from "../../../../calendar/components/EventFormModal";
import { fetchEventDetailsApi, fetchEventAssignmentsApi } from "../../../../calendar/services/calendarApi";
import { 
    Pencil, CalendarClock, Ban, Trash2, Download, Upload, X, Eye, 
    CheckCircle, FileSpreadsheet, Search, UserPlus, Calendar, ArrowRight, 
    AlertTriangle, PlusCircle 
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const norm       = (v) => String(v || "").trim().toLowerCase();
const toIsoLocal = (dt) => (dt ? String(dt).slice(0, 16) : "");
const pf         = (v)  => parseFloat(v) || 0;

const toDecimalOrNull = (s) => {
    if (!s && s !== 0) return null;
    const n = Number(String(s).trim().replace(",", "."));
    return Number.isNaN(n) ? null : String(n);
};
const toIntOrNull = (s) => {
    if (s == null || s === "") return null;
    const n = Number(String(s).trim());
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

const statusColor = (s) => ({
    PLANIFIE:  { bg: "#e8edf7", color: "#204385" },
    EN_COURS:  { bg: "#d1fae5", color: "#059669" },
    TERMINE:   { bg: "#f3f4f6", color: "#6b7280" },
    ANNULE:    { bg: "#fde8e7", color: "#ee645f" },
}[s] || { bg: "#f3f4f6", color: "#6b7280" });

const getLocationTypeLabel = (locationType) => ({
    ONSITE: "ONSITE",
    OFFSITE: "OFFSITE",
    VIRTUELLE: "VIRTUEL",
}[locationType] || locationType || "—");

const getReadableLocation = (event) =>
    event?.locationLabel
    || (event?.locationType === "OFFSITE" ? event?.offsiteLocation : null)
    || event?.salle?.nom
    || event?.offsiteLocation
    || "—";

const emptyForm = {
    titre: "", description: "", moduleCleId: "",
    locationType: "ONSITE", salleId: "", offsiteLocation: "",
    dateDebut: "", dateFin: "",
    allowRegistration: true, registrationDeadlineHours: "",
    isPeriodic: false, maxPlaces: "", currency: "MAD",
};

const emptyBudget = () => ({
    tarifFormation: 0, repas: 0, transport: 0,
    hebergement: 0, salleConference: 0, autres: 0, autreDescription: "",
});

const emptyReplan = { dateDebut: "", dateFin: "" };

// ─────────────────────────────────────────────────────────────────────────────
// Helper : calcul date expiration évaluation
// ─────────────────────────────────────────────────────────────────────────────
const calculateExpirationDate = (endDateStr, numberOfDays) => {
    if (!endDateStr) return null;
    const ms = numberOfDays * 24 * 60 * 60 * 1000;
    return new Date(new Date(endDateStr).getTime() + ms).toISOString();
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper : détecter le type HOT / COLD d'une évaluation
// S'adapte à l'ancienne nomenclature ("Evaluation à chaud" / "Evaluation à froid")
// comme à la nouvelle (typeCode "HOT" / "COLD")
// ─────────────────────────────────────────────────────────────────────────────
const isHot = (ev) =>
    ev.typeCode === "HOT" ||
    String(ev.evaluationType || "").toLowerCase().includes("chaud");

const isCold = (ev) =>
    ev.typeCode === "COLD" ||
    String(ev.evaluationType || "").toLowerCase().includes("froid");

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function EventManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ── Redux state ────────────────────────────────────────────────────────
    const eventsState      = useSelector((s) => s.events      || {});
    const modulesState     = useSelector((s) => s.modulesCle  || {});
    const sallesState      = useSelector((s) => s.salles      || {});
    const salles           = useSelector((s) => s.salles.salles);
    const consultantsState = useSelector((s) => s.consultants || {});
    const budgetState      = useSelector((s) => s.campagnes.budgets);
    const evaluationsState = useSelector((s) => s.evaluations || {});
    const assignmentsState = useSelector((s) => s.assignments || {});

    const evaluations = evaluationsState.evaluations || [];
    const events      = eventsState.events        || [];
    const modulesCle  = modulesState.modulesCle   || [];
    const consultants = consultantsState.consultants || [];

    const importResult = eventsState.importResult || null;
    const invitationResult = eventsState.invitationResult || null;

    // ── Import Excel ───────────────────────────────────────────────────────
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importAction,  setImportAction]  = useState("add");
    const [selectedFile,  setSelectedFile]  = useState(null);
    const [fileName,      setFileName]      = useState("");
    const fileInputRef = useRef(null);

    // ── Durées évaluations (partagées ajout individuel + import massif) ────
    const [numberOfDaysHot,  setNumberOfDaysHot]  = useState(1);
    const [numberOfDaysCold, setNumberOfDaysCold] = useState(120);
    const loading = !!(eventsState.loading || modulesState.loading || sallesState.loading || consultantsState.loading);

    // useEffect(() => {
    //     if (importResult && !loading) setIsPreviewOpen(true);
    // }, [importResult, loading]);

    useEffect(() => {
        if (importResult && !loading) {
            setImportModalOpen(false);
            setIsPreviewOpen(true);
        }
    }, [importResult, loading]);
    // ── Handlers import ───────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        setFileName(file.name);
    };

    const clearFile = () => {
        setSelectedFile(null);
        setFileName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const closeImportModal = () => {
        setImportModalOpen(false);
        clearFile();
        setImportAction("add");
        setSelectedEvaluationIds(new Set());
        setNumberOfDaysHot(1);
        setNumberOfDaysCold(120);
    };

    // const handleVerify = () => {
    //     if (!selectedFile) {
    //         Swal.fire({ icon: "warning", title: "Aucun fichier", text: "Sélectionnez un fichier Excel." });
    //         return;
    //     }
    //     dispatch(importEventsExcelRequest(selectedFile, true, importAction, numberOfDaysHot, numberOfDaysCold));
    // };
    const handleVerify = () => {
        if (!selectedFile) {
            Swal.fire({
                icon: "warning",
                title: "Aucun fichier",
                text: "Sélectionnez un fichier Excel."
            });
            return;
        }

        setImportModalOpen(false);

        dispatch(
            importEventsExcelRequest(
                selectedFile,
                true,
                importAction,
                Array.from(selectedEvaluationIds),
                numberOfDaysHot,
                numberOfDaysCold
            )
        );
    };
    // const handleConfirmImport = () => {
    //     if (!selectedFile) {
    //         Swal.fire({ icon: "warning", title: "Aucun fichier", text: "Sélectionnez un fichier Excel." });
    //         return;
    //     }
    //
    //     setIsPreviewOpen(false);
    //     dispatch(importEventsExcelRequest(selectedFile, false, importAction, numberOfDaysHot, numberOfDaysCold));
    //     closeImportModal();
    // };
    const handleConfirmImport = () => {
        if (!selectedFile) {
            Swal.fire({
                icon: "warning",
                title: "Aucun fichier",
                text: "Sélectionnez un fichier Excel."
            });
            return;
        }

        setIsPreviewOpen(false);

        dispatch(
            importEventsExcelRequest(
                selectedFile,
                false,
                importAction,
                Array.from(selectedEvaluationIds),
                numberOfDaysHot,
                numberOfDaysCold
            )
        );

        closeImportModal();
    };

    const campagneBudget  = budgetState || null;
    const montantCampagne = campagneBudget ? pf(campagneBudget.montant) : 0;
    const montantRestant  = campagneBudget
        ? (campagneBudget.budgetRestant != null
            ? pf(campagneBudget.budgetRestant)
            : pf(campagneBudget.montant))
        : 0;


    // ── Filtres ────────────────────────────────────────────────────────────
    const [tab, setTab] = useState("ALL");
    const [q,   setQ]   = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // ── Modale CRUD ────────────────────────────────────────────────────────
    const [open,      setOpen]      = useState(false);
    const [modalTab,  setModalTab]  = useState("INFOS");
    const [isEdit,    setIsEdit]    = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form,      setForm]      = useState(emptyForm);

    // Consultants
    const [consultSearch,         setConsultSearch]         = useState("");
    const [selectedConsultantIds, setSelectedConsultantIds] = useState(new Set());

    // Sessions
    const [seancesCount, setSeancesCount] = useState(0);
    const [seances,      setSeances]      = useState([]);

    // Budget
    const [budgetType, setBudgetType] = useState("formation");
    const [budget,     setBudget]     = useState(emptyBudget());

    // Réplanification
    const [replanOpen,    setReplanOpen]    = useState(false);
    const [replanEventId, setReplanEventId] = useState(null);
    const [replanForm,    setReplanForm]    = useState(emptyReplan);
    const [assignmentEvent, setAssignmentEvent] = useState(null);
    const [invitationModal, setInvitationModal] = useState(null);
    const [detailsEvent, setDetailsEvent] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const handleOpenDetails = async (eventItem) => {
        if (!eventItem?.id) {
            setDetailsEvent(eventItem);
            return;
        }

        setDetailsLoading(true);

        try {
            const detailsResponse = await fetchEventDetailsApi(eventItem.id);
            const fullEvent = detailsResponse?.data || detailsResponse || eventItem;

            let participants = [];

            if (Array.isArray(fullEvent?.participants)) {
                participants = fullEvent.participants;
            } else if (Array.isArray(eventItem?.participants)) {
                participants = eventItem.participants;
            }

            try {
                const assignmentsResponse = await fetchEventAssignmentsApi(eventItem.id);
                const fetchedParticipants = assignmentsResponse?.data || assignmentsResponse;
                if (Array.isArray(fetchedParticipants)) {
                    participants = fetchedParticipants;
                }
            } catch (assignError) {
                console.error("Erreur chargement affectations", assignError);
            }

            setDetailsEvent({
                ...eventItem,
                ...fullEvent,
                participants,
            });
        } catch (error) {
            console.error("Erreur chargement détails événement", error);
            setDetailsEvent(eventItem);
        } finally {
            setDetailsLoading(false);
        }
    };
    const [selectedEvaluationIds, setSelectedEvaluationIds] = useState(new Set());
    const [evaluationSearch,      setEvaluationSearch]      = useState("");

    const selectedEvent = useMemo(() => {
        if (!isEdit || !currentId) return null;
        return events.find((x) => x.id === currentId);
    }, [isEdit, currentId, events]);

    useEffect(() => {
        dispatch(fetchEventsRequest());
        dispatch(fetchModulesCleRequest());
        dispatch(fetchSallesRequest());
        dispatch(fetchConsultantsRequest());
        dispatch(getAllEvaluationsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (!invitationModal?.event?.id) return;
        dispatch(fetchAssignedUsersByEventRequest(invitationModal.event.id));
    }, [dispatch, invitationModal]);

    // ── Calculs budget ─────────────────────────────────────────────────────
    const totalCout = budgetType === "formation"
        ? pf(budget.tarifFormation)
        : pf(budget.repas) + pf(budget.transport) + pf(budget.hebergement)
        + pf(budget.salleConference) + pf(budget.autres);

    const budgetRestantCalc = montantRestant - totalCout;

    // ── Filtrage tableau ───────────────────────────────────────────────────
    const filteredEvents = useMemo(() => {
        const s = norm(q);
        return events.filter((e) => {
            const okTab    = tab === "ALL" || String(e.locationType) === tab;
            const okSearch = !s || norm(e.titre).includes(s) || norm(e.moduleCle && e.moduleCle.nom).includes(s)
                || norm(e.salle && e.salle.nom).includes(s) || norm(e.offsiteLocation).includes(s);
            return okTab && okSearch;
        });
    }, [events, tab, q]);

    const totalPages = Math.ceil(filteredEvents.length / itemsPerPage) || 1;

    const paginatedEvents = useMemo(() => {
        return filteredEvents.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredEvents, currentPage]);

    useEffect(() => {
        const total = Math.ceil(filteredEvents.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredEvents.length, currentPage]);

    const filteredConsultants = useMemo(() => {
        const s = norm(consultSearch);
        return consultants.filter((c) =>
            !s || norm(c.nomContact).includes(s) || norm(c.emailContact).includes(s) || norm(c.specialite).includes(s)
        );
    }, [consultants, consultSearch]);

    const invitationAssignedUsers = invitationModal?.event?.id
        ? (assignmentsState.assignedUsersByEvent?.[invitationModal.event.id] || [])
        : [];

    // ── Salles filtrées ────────────────────────────────────────────────────
    const sallesFiltrees = useMemo(() => {
        if (!salles) return [];
        if (form.locationType === "ONSITE")    return salles.filter((s) => s.type === "PHYSIQUE");
        if (form.locationType === "VIRTUELLE") return salles.filter((s) => s.type === "VIRTUELLE");
        return [];
    }, [salles, form.locationType]);

    // ── Sessions ───────────────────────────────────────────────────────────
    const handleSeancesCountChange = (value, existingSessions = []) => {
        setSeancesCount(value);
        const arr = [];
        for (let i = 1; i <= value; i++) {
            const ex = existingSessions[i - 1];
            arr.push({
                sorder:     ex ? ex.sorder     : i,
                name:       ex ? ex.name       : `Séance ${i}`,
                dureeHours: ex ? pf(ex.dureeHours) : 1,
                startat:    ex ? ex.startat    : null,
                endat:      ex ? ex.endat      : null,
                id:         ex ? ex.id         : null,
            });
        }
        setSeances(arr);
    };

    // ── Ouvrir / fermer modales ────────────────────────────────────────────
    const openAdd = () => {
        setOpen(true); setIsEdit(false); setCurrentId(null);
        setModalTab("INFOS"); setForm(emptyForm);
        setConsultSearch(""); setSelectedConsultantIds(new Set());
        setSeancesCount(0); setSeances([]);
        setBudgetType("formation"); setBudget(emptyBudget());
        setSelectedEvaluationIds(new Set());
        setEvaluationSearch("");
        setNumberOfDaysHot(1);
        setNumberOfDaysCold(120);
    };

    const openEdit = (e) => {
        setOpen(true); setIsEdit(true); setCurrentId(e.id);
        setModalTab("INFOS"); setConsultSearch("");
        setForm({
            ...emptyForm,
            titre:                     e.titre || "",
            description:               e.description || "",
            moduleCleId:               String((e.moduleCle && e.moduleCle.id) || ""),
            locationType:              e.locationType || "ONSITE",
            salleId:                   String((e.salle && e.salle.id) || ""),
            offsiteLocation:           e.offsiteLocation || "",
            dateDebut:                 toIsoLocal(e.dateDebut),
            dateFin:                   toIsoLocal(e.dateFin),
            allowRegistration:         e.allowRegistration !== false,
            registrationDeadlineHours: e.registrationDeadlineHours != null ? e.registrationDeadlineHours : "",
            isPeriodic:                !!e.isPeriodic,
            maxPlaces:                 e.maxPlaces != null ? e.maxPlaces : "",
            currency:                  e.currency || "MAD",
        });
        setBudget({
            tarifFormation:   pf(e.tarifFormation),
            repas:            pf(e.repas),
            transport:        pf(e.transport),
            hebergement:      pf(e.hebergement),
            salleConference:  pf(e.salleConference),
            autres:           pf(e.autres),
            autreDescription: e.autreDescription || "",
        });
        setSelectedConsultantIds(new Set((e.consultants || []).map((c) => c.id)));
        setSelectedEvaluationIds(new Set((e.evaluations || []).map((ev) => ev.id)));
        // Récupérer les durées stockées si disponibles, sinon valeurs par défaut
        setNumberOfDaysHot(e.numberOfDaysHot != null ? e.numberOfDaysHot : 1);
        setNumberOfDaysCold(e.numberOfDaysCold != null ? e.numberOfDaysCold : 120);
        const existingSessions = e.sessions || [];
        handleSeancesCountChange(existingSessions.length, existingSessions);
        if (e.moduleCle && e.moduleCle.campagne && e.moduleCle.campagne.id) {
            dispatch(fetchBudgetByCampagneRequest(e.moduleCle.campagne.id));
        }
    };

    const close = () => {
        setOpen(false); setForm(emptyForm);
        setSelectedConsultantIds(new Set()); setConsultSearch("");
        setSeancesCount(0); setSeances([]);
        setBudget(emptyBudget());
        setSelectedEvaluationIds(new Set());
        setEvaluationSearch("");
        setNumberOfDaysHot(1);
        setNumberOfDaysCold(120);
    };

    const toggleConsultant = (id) => {
        setSelectedConsultantIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleEvaluation = (id) => {
        setSelectedEvaluationIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const filteredEvaluations = useMemo(() => {
        const s = String(evaluationSearch || "").trim().toLowerCase();
        return evaluations.filter((ev) => {
            if (!s) return true;
            return (
                String(ev.code || "").toLowerCase().includes(s) ||
                String(ev.name || "").toLowerCase().includes(s) ||
                String(ev.typeCode || "").toLowerCase().includes(s) ||
                String(ev.evaluationType || "").toLowerCase().includes(s)
            );
        });
    }, [evaluations, evaluationSearch]);

    // ── Validation + payload ───────────────────────────────────────────────
    const validate = () => {
        if (!form.titre.trim())               return "Titre obligatoire";
        if (!form.moduleCleId)                return "Module obligatoire";
        if (!form.dateDebut || !form.dateFin) return "Dates obligatoires";
        if (form.locationType === "OFFSITE" && !form.offsiteLocation.trim())
            return "Lieu OFFSITE obligatoire";
        if (form.locationType !== "OFFSITE" && !form.salleId)
            return "Salle obligatoire (ONSITE/VIRTUELLE)";
        return null;
    };

    const buildPayload = () => {
        const coutTotal = pf(budget.tarifFormation) + pf(budget.repas) + pf(budget.transport)
            + pf(budget.hebergement) + pf(budget.salleConference) + pf(budget.autres);

        // Calcul des dates d'expiration des évaluations
        const dateDebutEvHot  = calculateExpirationDate(form.dateFin, numberOfDaysHot);
        const dateDebutEvCold = calculateExpirationDate(form.dateFin, numberOfDaysCold);

        return {
            titre:                     form.titre.trim(),
            description:               form.description ? form.description.trim() : null,
            moduleCleId:               Number(form.moduleCleId),
            locationType:              form.locationType,
            salleId:                   form.locationType === "OFFSITE" ? null : Number(form.salleId),
            offsiteLocation:           form.locationType === "OFFSITE" ? form.offsiteLocation.trim() : null,
            dateDebut:                 form.dateDebut ? new Date(form.dateDebut).toISOString() : null,
            dateFin:                   form.dateFin   ? new Date(form.dateFin).toISOString()   : null,
            allowRegistration:         !!form.allowRegistration,
            registrationDeadlineHours: form.allowRegistration ? toIntOrNull(form.registrationDeadlineHours) : null,
            isPeriodic:                !!form.isPeriodic,
            maxPlaces:                 toIntOrNull(form.maxPlaces),
            currency:                  form.currency || "MAD",
            tarifFormation:            toDecimalOrNull(budget.tarifFormation),
            repas:                     toDecimalOrNull(budget.repas),
            transport:                 toDecimalOrNull(budget.transport),
            hebergement:               toDecimalOrNull(budget.hebergement),
            salleConference:           toDecimalOrNull(budget.salleConference),
            autres:                    toDecimalOrNull(budget.autres),
            autreDescription:          budget.autreDescription || null,
            cout:                      toDecimalOrNull(coutTotal),
            budgetRestant:             toDecimalOrNull(budgetRestantCalc),
            consultantIds:             Array.from(selectedConsultantIds),
            evaluationIds:             Array.from(selectedEvaluationIds),
            numberOfDaysHot,
            numberOfDaysCold,
            dateDebutEvHot,
            dateDebutEvCold,
            sessions: seances.map((s) => ({
                id:         s.id || null,
                sorder:     s.sorder,
                name:       s.name,
                dureeHours: s.dureeHours,
                startat:    s.startat ? new Date(s.startat).toISOString() : null,
                endat:      s.endat   ? new Date(s.endat).toISOString()   : null,
            })),
        };
    };

    const submit = () => {
        const err = validate();
        if (err) { Swal.fire({ icon: "warning", text: err, timer: 2000, showConfirmButton: false }); return; }
        const payload = buildPayload();
        if (isEdit && currentId) dispatch(updateEventRequest(currentId, payload));
        else                     dispatch(createEventRequest(payload));
        close();
    };

    // ── Actions tableau ────────────────────────────────────────────────────
    const onDelete = async (id) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Supprimer cet événement ?",
            text: "Cette action est irréversible.",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#6b7280",
        });
        if (result.isConfirmed) dispatch(deleteEventRequest(id));
    };

    const onCancel = async (id) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Annuler cet événement ?",
            text: "L'événement sera conservé avec le statut ANNULÉ.",
            showCancelButton: true,
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Fermer",
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#6b7280",
        });
        if (result.isConfirmed) dispatch(cancelEventRequest(id));
    };


    const openReplan = (ev) => {
        setReplanEventId(ev.id);
        setReplanForm({ dateDebut: toIsoLocal(ev.dateDebut), dateFin: toIsoLocal(ev.dateFin) });
        setReplanOpen(true);
    };

    const openAssignmentModal = (eventItem) => {
        setAssignmentEvent(eventItem);
    };

    const openInvitationModal = (eventItem, actionType) => {
        setInvitationModal({ event: eventItem, actionType });
    };

    const submitEventCalendarAction = () => {
        if (!invitationModal?.event?.id || !invitationModal?.actionType) return;
        dispatch(sendEventCalendarRequest(invitationModal.event.id, invitationModal.actionType));
        setInvitationModal(null);
    };

    const submitReplan = () => {
        if (!replanForm.dateDebut || !replanForm.dateFin) {
            Swal.fire({ icon: "warning", text: "Dates obligatoires", timer: 1800, showConfirmButton: false });
            return;
        }
        dispatch(replanifyEventRequest({
            id:        replanEventId,
            dateDebut: new Date(replanForm.dateDebut).toISOString(),
            dateFin:   new Date(replanForm.dateFin).toISOString(),
        }));
        setReplanOpen(false);
    };

    const invitationActionLabels = {
        SEND_INITIAL_INVITATION: {
            title: "Envoyer invitation Outlook",
            cta: "Envoyer invitation",
            hint: "Une invitation calendrier ICS sera envoyée aux users affectés en destinataires principaux, avec les managers, consultants et fournisseurs en copie.",
        },
        RESCHEDULE_EVENT: {
            title: "Envoyer mise à jour Outlook",
            cta: "Envoyer mise à jour",
            hint: "Une mise à jour ICS avec le même UID et une séquence incrémentée sera envoyée aux mêmes destinataires.",
        },
        CANCEL_EVENT: {
            title: "Envoyer annulation Outlook",
            cta: "Envoyer annulation",
            hint: "Une annulation ICS compatible Outlook sera envoyée. L’événement sera aussi marqué annulé côté back.",
        },
    };

    const invitationUi = invitationModal?.actionType
        ? invitationActionLabels[invitationModal.actionType]
        : invitationActionLabels.SEND_INITIAL_INVITATION;

    // ─────────────────────────────────────────────────────────────────────
    // RENDU
    // ─────────────────────────────────────────────────────────────────────
    return (
        <div className="ev-page">

            {/* ── En-tête ──────────────────────────────────────────────── */}
            <div className="ev-header">
                <div className="ev-header-info">
                    <h2>Gestion des événements</h2>
                </div>
                <div className="ev-header-btns">
                    <button className="ev-btn ev-btn-primary" onClick={openAdd}>
                        <PlusCircle size={15} /> Ajouter événement
                    </button>
                    <button
                        className="ev-btn ev-btn-light"
                        onClick={() => dispatch(downloadEventsTemplateRequest())}
                        disabled={loading}
                        title="Télécharger le template"
                    >
                        <FileSpreadsheet size={14} /> Template
                    </button>
                    <button
                        className="ev-btn ev-btn-light"
                        onClick={() => dispatch(exportEventsExcelRequest())}
                        disabled={loading}
                        title="Exporter tous les événements"
                    >
                        <Download size={14} /> Exporter
                    </button>
                    <button
                        className="ev-btn ev-btn-primary"
                        onClick={() => setImportModalOpen(true)}
                    >
                        <Upload size={14} /> Importer
                    </button>
                </div>
            </div>

            {/* ── Toolbar ──────────────────────────────────────────────── */}
            <div className="ev-toolbar">
                <div className="event-management-tabs">
                    {[
                        { key: "ALL",       label: "Tous" },
                        { key: "ONSITE",    label: "Onsite" },
                        { key: "OFFSITE",   label: "Offsite" },
                        { key: "VIRTUELLE", label: "Virtuelle" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className={`event-management-tab ${tab === t.key ? "is-active" : ""}`}
                            onClick={() => { setTab(t.key); setCurrentPage(1); }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="ev-search-wrap">
                    <Search size={14} className="ev-search-icon" />
                    <input
                        className="ev-search"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setCurrentPage(1); }}
                        placeholder="Rechercher un événement…"
                    />
                </div>
            </div>

            {/* ── Tableau ──────────────────────────────────────────────── */}
            <div className="ev-card">
                <div className="ev-table-wrap">
                    {loading && <div className="ev-loading">Chargement…</div>}
                    <table className="ev-table">
                        <thead>
                            <tr>
                                <th>Titre</th>
                                <th>Statut</th>
                                <th>Lieu</th>
                                {/*<th>Module</th>*/}
                                <th>Période</th>
                                {/*<th>Évals.</th>*/}
                                <th>Sess.</th>
                                <th style={{ width: 140, textAlign: "center" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEvents.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: "center", padding: 32, opacity: 0.6 }}>
                                        Aucun événement trouvé
                                    </td>
                                </tr>
                            ) : paginatedEvents.map((e) => (
                                <tr key={e.id} style={{ opacity: e.status === "ANNULE" ? 0.6 : 1 }}>
                                    <td>
                                        <div style={{ fontWeight: 700, color: "#204385" }}>{e.titre}</div>
                                        <div className="ev-hint" style={{ fontSize: 10 }}>{e.reference}</div>
                                    </td>
                                    <td>
                                        <span className={`ev-pill ${e.status?.toLowerCase().replace("_", "-")}`}>
                                            {e.status || "—"}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <span className="ev-badge ev-badge--blue">{getLocationTypeLabel(e.locationType)}</span>
                                            <span className="ev-hint" style={{ fontSize: 10, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {getReadableLocation(e)}
                                            </span>
                                        </div>
                                    </td>
                                    {/*<td>*/}
                                    {/*    <div style={{ fontSize: 11 }}>{e.moduleCleNom}</div>*/}
                                    {/*    {e.campagneNom && <div className="ev-hint" style={{ fontSize: 9 }}>{e.campagneNom}</div>}*/}
                                    {/*</td>*/}
                                    <td>
                                        <div style={{ fontSize: 10 }}>
                                            {e.dateDebut?.slice(0, 10)}
                                            <ArrowRight size={10} style={{ margin: "0 4px" }} />
                                            {e.dateFin?.slice(0, 10)}
                                        </div>
                                    </td>
                                    {/*<td>*/}
                                    {/*    <span className="ev-badge ev-badge--soft-blue">{e.evaluationCount || 0}</span>*/}
                                    {/*</td>*/}
                                    <td>
                                        <span className="ev-badge ev-badge--soft-red">{e.sessions?.length || 0}</span>
                                    </td>
                                    <td>
                                        <div className="ev-btn-group">
                                            <button className="ev-icon-btn" onClick={() => openEdit(e)} title="Modifier">
                                                <Pencil size={14} />
                                            </button>
                                            <button 
                                                className="ev-icon-btn" 
                                                onClick={() => handleOpenDetails(e)} 
                                                title="Voir détails"
                                                disabled={detailsLoading}
                                            >
                                                <Eye size={14} />
                                            </button>
                                            <button className="ev-icon-btn" onClick={() => openReplan(e)} title="Réplanifier">
                                                <CalendarClock size={14} />
                                            </button>
                                            <button className="ev-icon-btn" onClick={() => setAssignmentEvent(e)} title="Affectations">
                                                <UserPlus size={14} />
                                            </button>
                                            <button
                                                className="ev-icon-btn"
                                                onClick={() => navigate(`/formation/responsable/evenements/${e.id}/absences`)}
                                                title="Gerer absences"
                                            >
                                                <AlertTriangle size={14} />
                                            </button>
                                            <button
                                                className="ev-icon-btn"
                                                onClick={() => dispatch(downloadEventAttendanceSheetRequest(e.id))}
                                                title="Feuille d'émargement"
                                            >
                                                <Download size={14} />
                                            </button>
                                            <button 
                                                className="ev-icon-btn" 
                                                onClick={() => setInvitationModal({ event: e, actionType: "SEND_INITIAL_INVITATION" })} 
                                                title="Invitation Outlook"
                                            >
                                                <Calendar size={14} />
                                            </button>
                                            {e.status !== "ANNULE" && (
                                                <button className="ev-icon-btn ev-icon-btn--danger" onClick={() => onCancel(e.id)} title="Annuler">
                                                    <Ban size={14} />
                                                </button>
                                            )}
                                            <button className="ev-icon-btn ev-icon-btn--danger" onClick={() => onDelete(e.id)} title="Supprimer">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredEvents.length > 0 && (
                    <div className="cicada-pagination" style={{ margin: "12px 14px" }}>
                        <div className="cicada-pagination-info">
                            Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredEvents.length)} sur {filteredEvents.length} événements
                        </div>

                        <div className="cicada-pagination-actions">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            >
                                Précédent
                            </button>

                            <span>
                                Page {currentPage} / {totalPages}
                            </span>

                            <button
                                type="button"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            >
                                Suivant
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <EventDetailsModal
                event={detailsEvent}
                onClose={() => setDetailsEvent(null)}
            />

            {/* ── MODALE IMPORT MASSIF ───────────────────────────────────────────── */}
            {importModalOpen && (
                <div className="ev-modal-overlay" onClick={closeImportModal}>
                    <div className="ev-modal ev-modal--medium" onClick={(e) => e.stopPropagation()}>
                        <div className="ev-modal-header">
                            <h3><Upload size={16} /> Import massif</h3>
                            <button className="ev-x" onClick={closeImportModal}><X size={18} /></button>
                        </div>
                        <div className="ev-modal-body">
                            <div className="ev-box">
                                <p className="ev-section-title">Fichier Excel</p>
                                <div className="ev-import-row">
                                    <label className={`ev-file-label ${fileName ? "has-file" : ""}`}>
                                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: "none" }} />
                                        <Upload size={14} />
                                        <span>{fileName || "Choisir un fichier"}</span>
                                    </label>
                                    <select style={{ width: 120, height: 34 }} value={importAction} onChange={(e) => setImportAction(e.target.value)}>
                                        <option value="add">Ajout</option>
                                        <option value="edit">Mise à jour</option>
                                    </select>
                                </div>
                            </div>
                            <div className="ev-box">
                                <p className="ev-section-title">Évaluations par défaut</p>
                                <div className="ev-import-eval-grid">
                                    {(evaluations || [])
                                        .filter((ev) => ev?.typeCode === "HOT" || ev?.typeCode === "COLD")
                                        .map((ev) => (
                                            <div key={ev.id} className="ev-import-eval-card">
                                                <label className="ev-toggle">
                                                    <input type="checkbox" checked={selectedEvaluationIds.has(ev.id)} onChange={() => toggleEvaluation(ev.id)} />
                                                    <span className="ev-slider" />
                                                </label>
                                                <div className="ev-import-eval-info">
                                                    <span>{(ev.name || ev.nom) }</span>
                                                    <span className="ev-badge ev-badge--soft-blue" style={{ fontSize: 9 }}>{isHot(ev) ? "À chaud" : "À froid"}</span>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="ev-modal-footer">
                            <button className="ev-btn ev-btn-light" onClick={closeImportModal}>Annuler</button>
                            <button className="ev-btn ev-btn-light" onClick={handleVerify} disabled={loading || !selectedFile}><Eye size={14} /> Vérifier</button>
                            <button className="ev-btn ev-btn-primary" onClick={handleConfirmImport} disabled={loading || !selectedFile}><Upload size={14} /> Importer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODALE PREVIEW DRY RUN ──────────────────────────────────────────── */}
            {isPreviewOpen && importResult && (
                <div className="ev-modal-overlay" onClick={() => setIsPreviewOpen(false)}>
                    <div className="ev-modal ev-modal--large" onClick={(e) => e.stopPropagation()}>
                        <div className="ev-modal-header">
                            <h3><CheckCircle size={16} /> Résultat de vérification</h3>
                            <button className="ev-x" onClick={() => setIsPreviewOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="ev-modal-body">
                            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                                <div style={{ background: "#d1fae5", padding: "8px 12px", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                    <CheckCircle size={14} style={{ color: "#059669" }} />
                                    <span style={{ fontSize: 12, color: "#065f46" }}>{importResult.createdCount} Prêts</span>
                                </div>
                                <div style={{ background: "#fde8e7", padding: "8px 12px", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                    <AlertTriangle size={14} style={{ color: "#ee645f" }} />
                                    <span style={{ fontSize: 12, color: "#9b1c1c" }}>{importResult.skippedCount} Erreurs</span>
                                </div>
                            </div>
                            {importResult.errors?.length > 0 && (
                                <div className="ev-box" style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
                                    <p className="ev-section-title" style={{ color: "#9b1c1c" }}>Détails des erreurs</p>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {importResult.errors.map((err, i) => (
                                            <div key={i} style={{ fontSize: 11, color: "#9b1c1c" }}>
                                                Ligne {err.row} : <b>{err.field}</b> - {err.message}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {importResult.preview?.length > 0 && (
                                <div className="ev-table-wrap">
                                    <table className="ev-table" style={{ fontSize: 10 }}>
                                        <thead>
                                            <tr><th>Réf.</th><th>Titre</th><th>Module</th><th>Type de lieu</th><th>Lieu / Salle</th><th>Début</th><th>Fin</th></tr>
                                        </thead>
                                        <tbody>
                                            {importResult.preview.map((row, i) => (
                                                <tr key={i}>
                                                    <td>{row.reference}</td>
                                                    <td style={{ fontWeight: 700 }}>{row.titre}</td>
                                                    <td>{row.moduleCleNom}</td>
                                                    <td>{getLocationTypeLabel(row.locationType)}</td>
                                                    <td>{getReadableLocation(row)}</td>
                                                    <td>{row.dateDebut?.slice(0, 16)}</td>
                                                    <td>{row.dateFin?.slice(0, 16)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="ev-modal-footer">
                            <button className="ev-btn ev-btn-light" onClick={() => setIsPreviewOpen(false)}>Annuler</button>
                            <button className="ev-btn ev-btn-primary" onClick={handleConfirmImport} disabled={loading || importResult.createdCount === 0}>
                                Confirmer l'import ({importResult.createdCount})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODALE INVITATION OUTLOOK ───────────────────────────────────────── */}
            {invitationModal && (
                <div className="ev-modal-overlay" onClick={() => setInvitationModal(null)}>
                    <div className="ev-modal ev-modal--small" onClick={(e) => e.stopPropagation()}>
                        <div className="ev-modal-header">
                            <h3>{invitationUi.title}</h3>
                            <button className="ev-x" onClick={() => setInvitationModal(null)}><X size={18} /></button>
                        </div>
                        <div className="ev-modal-body">
                            <div className="ev-grid">
                                <div className="ev-field ev-span-2">
                                    <label>Événement</label>
                                    <input value={invitationModal.event.titre || ""} disabled className="ev-input" />
                                </div>
                                <div className="ev-field">
                                    <label>Affectés</label>
                                    <input value={invitationAssignedUsers.length} disabled className="ev-input" />
                                </div>
                                <div className="ev-field">
                                    <label>Période</label>
                                    <input style={{ fontSize: 10 }} value={`${invitationModal.event.dateDebut?.slice(0, 16)} → ${invitationModal.event.dateFin?.slice(0, 16)}`} disabled className="ev-input" />
                                </div>
                                <div className="ev-span-2" style={{ padding: "12px", background: "#f8f9fa", borderRadius: 8 }}>
                                    <p className="ev-hint" style={{ lineHeight: 1.6 }}>{invitationUi.hint}</p>
                                </div>
                            </div>
                        </div>
                        <div className="ev-modal-footer">
                            <button className="ev-btn ev-btn-light" onClick={() => setInvitationModal(null)}>Annuler</button>
                            <button className="ev-btn ev-btn-primary" onClick={submitEventCalendarAction} disabled={loading}>
                                {loading ? "Envoi..." : invitationUi.cta}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <EventFormModal
                open={open}
                onClose={close}
                mode={isEdit ? "edit" : "create"}
                event={selectedEvent}
                modules={modulesCle}
                salles={sallesFiltrees}
                loading={loading}
                onSubmit={(payload) => {
                    if (isEdit && selectedEvent) {
                        dispatch(updateEventRequest(selectedEvent.id, payload));
                    } else {
                        dispatch(createEventRequest(payload));
                    }
                    close();
                }}
            />

            {/* ── MODALE RÉPLANIFICATION ────────────────────────────────────────── */}
            {replanOpen && (
                <div className="ev-modal-overlay" onClick={() => setReplanOpen(false)}>
                    <div className="ev-modal ev-modal--small" onClick={(e) => e.stopPropagation()}>
                        <div className="ev-modal-header">
                            <h3>Réplanifier l'événement</h3>
                            <button className="ev-x" onClick={() => setReplanOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="ev-modal-body">
                            <div className="ev-grid">
                                <div className="ev-field">
                                    <label>Début *</label>
                                    <input type="datetime-local" className="ev-input" value={replanForm.dateDebut} onChange={(e) => setReplanForm({ ...replanForm, dateDebut: e.target.value })} />
                                </div>
                                <div className="ev-field">
                                    <label>Fin *</label>
                                    <input type="datetime-local" className="ev-input" value={replanForm.dateFin} onChange={(e) => setReplanForm({ ...replanForm, dateFin: e.target.value })} />
                                </div>
                                <div className="ev-span-2 ev-hint" style={{ marginTop: 8 }}>
                                    Les séances seront régénérées (09:00 - 17:00 par défaut).
                                </div>
                            </div>
                        </div>
                        <div className="ev-modal-footer">
                            <button className="ev-btn ev-btn-light" onClick={() => setReplanOpen(false)}>Annuler</button>
                            <button className="ev-btn ev-btn-primary" onClick={submitReplan}>Réplanifier</button>
                        </div>
                    </div>
                </div>
            )}

            <BulkAssignmentModal
                open={!!assignmentEvent}
                onClose={() => setAssignmentEvent(null)}
                assignmentType="EVENT"
                targetId={assignmentEvent?.id}
                targetLabel={assignmentEvent?.titre}
            />
        </div>
    );
}
